'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ActiveUpgradePrompt } from '../../services/client/ClientUpgradeService';
import Button from '../form/Button';

export type UpgradeBannerProps = {
	prompt: ActiveUpgradePrompt;
	onDismiss?: () => void;
	onEngage: (promptId: string) => Promise<string | null>;
};

export default function UpgradeBanner({ prompt, onDismiss, onEngage }: UpgradeBannerProps) {
	const { title, message, ctaText } = prompt.prompt;

	// Fallback for empty CTA text to ensure button always has content
	const displayCtaText = ctaText || 'Upgrade';

	const bannerRef = useRef<HTMLDivElement>(null);
	const [isClient, setIsClient] = useState(false);

	useEffect(() => {
		setIsClient(true);
	}, []);

	// Body class + banner height CSS var
	useEffect(() => {
		if (!isClient) {
			return;
		}

		const bodyClass = 'upgrade-banner-visible';

		const updateHeight = () => {
			const height = bannerRef.current?.getBoundingClientRect().height ?? 92;
			document.documentElement.style.setProperty('--upgrade-banner-height', `${height}px`);
		};

		updateHeight();
		document.body.classList.add(bodyClass);

		const ro = new ResizeObserver(() => updateHeight());
		if (bannerRef.current) {
			ro.observe(bannerRef.current);
		}

		return () => {
			document.body.classList.remove(bodyClass);
			document.documentElement.style.removeProperty('--upgrade-banner-height');
			ro.disconnect();
		};
	}, [isClient]);

	const handleCtaClick = useCallback(async () => {
		let engagementId: string | null = null;

		try {
			engagementId = await onEngage(prompt.prompt.id);
		} catch (error) {
			console.error('[UpgradeBanner] Failed to record engagement:', error);
		} finally {
			const href = engagementId ? `/upgrade?engagementId=${encodeURIComponent(engagementId)}` : '/upgrade';
			window.location.assign(href);
		}
	}, [onEngage, prompt.prompt.id]);

	// Avoid SSR/hydration issues
	if (!isClient) {
		return null;
	}

	return createPortal(
		<div ref={bannerRef} className="upgrade-banner" role="region" aria-label="Upgrade notice">
			<div className="upgrade-banner__content">
				<div className="upgrade-banner__text">
					{title && <h3 className="upgrade-banner__title">{title}</h3>}
					{message && (
						<div className="upgrade-banner__message" dangerouslySetInnerHTML={{ __html: message }} />
					)}

					{/* Desktop button - inside text for centering */}
					<Button
						type="button"
						className="Button solid dark small reverse outline desktop-only"
						onClick={handleCtaClick}
					>
						{displayCtaText}
					</Button>
				</div>

				<div className="upgrade-banner__actions">
					{/* Mobile button */}
					<Button
						type="button"
						className="Button solid dark small reverse outline mobile-only"
						onClick={handleCtaClick}
					>
						{displayCtaText}
					</Button>

					{onDismiss && (
						<button
							type="button"
							className="upgrade-banner__close flex jc ac"
							onClick={onDismiss}
							aria-label="Dismiss banner"
						>
							{/* eslint-disable-next-line @next/next/no-img-element */}
							<img src="/images/icons/dark-icons/Exit.svg" alt="" aria-hidden />
						</button>
					)}
				</div>
			</div>
		</div>,
		document.body,
	);
}
